import axios from "axios";
import { adminApi } from "../../lib/axios";

export type ScoringMode = "ORDINAL" | "NORMALIZED";
export type ScoringWindow = "LIFETIME" | "LAST_30_DAYS" | "LAST_90_DAYS";

export interface RankingConfig {
  enabled: boolean;
  revenueWeight: number;
  commissionWeight: number;
  ordersWeight: number;
  affiliatesWeight: number;
  scoringMode: ScoringMode;
  scoringWindow: ScoringWindow;
  requireActiveSession: boolean;
  requireOnboardingCompleted: boolean;
  excludeTestAccounts: boolean;
  minAffiliates: number;
  minOrders: number;
  revenueLookbackDays: number;
  skipMetricSyncForPinned: boolean;
}

export interface RankingScheduleSettings {
  enabled: boolean;
  everyMs: number;
  cron: string | null;
  timezone: string | null;
  syncConcurrency: number;
}

export interface RankingConfigResponse {
  config: RankingConfig;
  updatedAt: string | null;
  updatedBy: string | null;
  schedule: RankingScheduleSettings;
}

export interface RankingChange {
  id: string;
  companyName: string | null;
  from: number | null;
  to: number;
  pinned: boolean;
}

export interface RankingRun {
  id: string;
  trigger: "SCHEDULED" | "MANUAL" | "DRY_RUN";
  status: "RUNNING" | "COMPLETED" | "FAILED";
  dryRun: boolean;
  startedAt: string;
  finishedAt: string | null;
  eligibleCount: number;
  pinnedCount: number;
  rankedCount: number;
  changedCount: number;
  syncedCount: number;
  syncSkippedCount: number;
  syncFailedCount: number;
  shopifyOrdersFetched: number;
  changes: RankingChange[] | null;
  warnings: string[] | null;
  error: string | null;
  triggeredBy: string | null;
}

export interface RankingStatus {
  schedule: RankingScheduleSettings & {
    registered: boolean;
    nextRunAt: string | null;
  };
  activeRun: RankingRun | null;
  lastRun: RankingRun | null;
  brands: { total: number; pinned: number; unpinned: number };
  revenueNeverSynced: number;
}

const fail = (error: unknown, fallback: string): never => {
  if (axios.isAxiosError(error)) {
    throw new Error(error.response?.data?.message || fallback);
  }
  throw new Error("An unexpected error occurred");
};

class BrandRankingAdminService {
  async getConfig(): Promise<RankingConfigResponse> {
    try {
      const response = await adminApi.get("/admin/brand-ranking/config");
      return response.data;
    } catch (error) {
      return fail(error, "Failed to load ranking configuration");
    }
  }

  async updateConfig(patch: Partial<RankingConfig>): Promise<RankingConfigResponse> {
    try {
      const response = await adminApi.patch("/admin/brand-ranking/config", patch);
      return response.data;
    } catch (error) {
      return fail(error, "Failed to save ranking configuration");
    }
  }

  async getStatus(): Promise<RankingStatus> {
    try {
      const response = await adminApi.get("/admin/brand-ranking/status");
      return response.data;
    } catch (error) {
      return fail(error, "Failed to load ranking status");
    }
  }

  async listRuns(limit = 20): Promise<RankingRun[]> {
    try {
      const response = await adminApi.get(`/admin/brand-ranking/runs?limit=${limit}`);
      return response.data;
    } catch (error) {
      return fail(error, "Failed to load ranking runs");
    }
  }

  async triggerRun(): Promise<{ jobId: string; queued: boolean }> {
    try {
      const response = await adminApi.post("/admin/brand-ranking/run");
      return response.data;
    } catch (error) {
      return fail(error, "Failed to queue ranking run");
    }
  }

  async dryRun(): Promise<RankingRun> {
    try {
      const response = await adminApi.post("/admin/brand-ranking/dry-run");
      return response.data;
    } catch (error) {
      return fail(error, "Dry run failed");
    }
  }

  async applyPins(): Promise<RankingRun> {
    try {
      const response = await adminApi.post("/admin/brand-ranking/apply-pins");
      return response.data;
    } catch (error) {
      return fail(error, "Failed to apply pins");
    }
  }

  async pinBrands(brandIds: string[], pinned: boolean): Promise<{ updated: number; pinned: boolean }> {
    try {
      const response = await adminApi.patch("/admin/brand-ranking/brands/pin", { brandIds, pinned });
      return response.data;
    } catch (error) {
      return fail(error, pinned ? "Failed to pin brands" : "Failed to unpin brands");
    }
  }

  async setBrandRank(brandId: string, rank: number): Promise<RankingRun> {
    try {
      const response = await adminApi.patch(`/admin/brand-ranking/brands/${brandId}/rank`, { rank });
      return response.data;
    } catch (error) {
      return fail(error, "Failed to set brand rank");
    }
  }
}

export const brandRankingAdminService = new BrandRankingAdminService();
